import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import CartPanel from '../../components/CartPanel';

type Product = {
  id: number;
  name: string;
  slug: string;
  category: 'hoodies' | 't-shirts' | 'hats' | 'stickers' | 'drinkware';
  price: number;
  sizes?: string[];
  description: string;
  image: string;
  featured?: boolean;
};

const products: Product[] = [
  {
    id: 1,
    name: 'Rides & Dives Classic Hoodie',
    slug: 'classic-hoodie',
    category: 'hoodies',
    price: 59.99,
    sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL'],
    description: 'Premium heavyweight hoodie with embroidered logo. Perfect for cold morning rides.',
    image: 'https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/7a9603ccf45559a5ef82ce4889be98a2.png',
    featured: true,
  },
  {
    id: 2,
    name: 'Mountain Roads T-Shirt',
    slug: 'mountain-roads-tee',
    category: 't-shirts',
    price: 29.99,
    sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL'],
    description: 'Soft cotton tee with vintage mountain graphic. Breathable and comfortable for all-day wear.',
    image: 'https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/1b51d62f43e9e1433fd243e7a44ba490.png',
    featured: true,
  },
  {
    id: 3,
    name: 'Dive Bar Snapback',
    slug: 'dive-bar-snapback',
    category: 'hats',
    price: 34.99,
    description: 'Classic snapback with embroidered patch. One size fits most.',
    image: 'https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/99c990e8115b922351d8c93d29a30082.png',
    featured: false,
  },
  {
    id: 4,
    name: 'Rides & Dives Sticker Pack',
    slug: 'sticker-pack',
    category: 'stickers',
    price: 1.99,
    description: 'Set of 5 weatherproof vinyl stickers. Perfect for helmets, bikes, and laptops.',
    image: 'https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/0d00e3c69e7442cc4445e864a5c497b0.png',
    featured: false,
  },
  {
    id: 5,
    name: 'Rides & Dives YETI Tumbler',
    slug: 'yeti-tumbler',
    category: 'drinkware',
    price: 44.99,
    description: 'Premium 20oz YETI tumbler with laser-engraved logo. Keeps drinks cold for 24 hours or hot for 12 hours.',
    image: 'https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/5dd0dad30b94f54cd5d989e39f0fc07d.png',
    featured: true,
  },
];

export default function MerchPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [mobileSocialsOpen, setMobileSocialsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [cartOpen, setCartOpen] = useState(false);

  const { addItem, totalItems } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = [
    { id: 'all', name: 'All Merch' },
    { id: 'hoodies', name: 'Hoodies' },
    { id: 't-shirts', name: 'T-Shirts' },
    { id: 'hats', name: 'Hats' },
    { id: 'stickers', name: 'Stickers' },
  ];

  const filteredProducts = activeCategory === 'all' 
    ? products 
    : products.filter(item => item.category === activeCategory);

  const handleAddToCart = (product: Product) => {
    if (product.sizes && !selectedSize) {
      alert('Please select a size');
      return;
    }
    
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      size: selectedSize || undefined,
      image: product.image,
    });

    setSelectedProduct(null);
    setSelectedSize('');
    setCartOpen(true);
  };

  return (
    <div className="bg-black text-white min-h-screen">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black' : 'bg-black/90'}`}>
        <div className="flex items-center justify-between h-20 md:h-24 px-4 md:px-8">
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/1d9414342dd58a00fdde5ee22dbe4d3e.png" 
              alt="Rides & Dives Logo" 
              className="h-16 md:h-20 w-auto"
            />
          </Link>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCartOpen(true)}
              className="relative text-white hover:text-[#8B6F47] transition-colors cursor-pointer"
            >
              <i className="ri-shopping-cart-line text-2xl"></i>
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#8B6F47] text-black text-xs font-bold rounded-full">
                  {totalItems}
                </span>
              )}
            </button>

            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white text-2xl cursor-pointer"
            >
              <i className={mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>

          <div className="hidden lg:flex items-center gap-8 xl:gap-12">
            <Link to="/rides" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Rides</Link>
            <Link to="/dives" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Dives</Link>
            <Link to="/gear" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Gear</Link>
            <Link to="/gallery" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Gallery</Link>
            <Link to="/merch" className="text-sm font-light tracking-widest uppercase text-[#8B6F47] cursor-pointer whitespace-nowrap">Merch</Link>
            <Link to="/trip-planner" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Plan</Link>
            <div 
              className="relative"
              onMouseEnter={() => setSocialsOpen(true)}
              onMouseLeave={() => setSocialsOpen(false)}
            >
              <button 
                className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                Follow
                <i className={`ri-arrow-down-s-line transition-transform ${socialsOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {socialsOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                  <div className="bg-black border border-white/10 py-2 min-w-[160px]">
                    <a href="https://www.youtube.com/@RidesDives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-youtube-fill text-lg"></i>
                      YouTube
                    </a>
                    <a href="https://www.instagram.com/rides.and.dives/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-instagram-fill text-lg"></i>
                      Instagram
                    </a>
                    <a href="https://www.tiktok.com/@ridesanddives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-tiktok-fill text-lg"></i>
                      TikTok
                    </a>
                    <a href="https://www.facebook.com/share/g/1AqygJbGTi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-facebook-fill text-lg"></i>
                      Facebook
                    </a>
                    <Link to="/blog" className="flex items-center gap-3 px-4 py-2 text-sm hover:text-[#8B6F47] hover:bg-white/5 transition-colors cursor-pointer">
                      <i className="ri-article-line text-lg"></i>
                      Blog
                    </Link>
                  </div>
                </div>
              )}
            </div>
            <Link to="/about" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">About</Link>
            <Link to="/contact" className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap">Contact</Link>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-black border-t border-white/10">
            <div className="flex flex-col py-4 px-4 space-y-4">
              <Link to="/rides" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Rides</Link>
              <Link to="/dives" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Dives</Link>
              <Link to="/gear" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Gear</Link>
              <Link to="/gallery" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Gallery</Link>
              <Link to="/merch" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase text-[#8B6F47] cursor-pointer py-2">Merch</Link>
              <Link to="/trip-planner" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Plan</Link>
              <div>
                <button 
                  onClick={() => setMobileSocialsOpen(!mobileSocialsOpen)}
                  className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2 flex items-center gap-1 w-full"
                >
                  Follow
                  <i className={`ri-arrow-down-s-line transition-transform ${mobileSocialsOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileSocialsOpen && (
                  <div className="pl-4 space-y-3 mt-2">
                    <a href="https://www.youtube.com/@RidesDives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-youtube-fill text-lg"></i>
                      YouTube
                    </a>
                    <a href="https://www.instagram.com/rides.and.dives/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-instagram-fill text-lg"></i>
                      Instagram
                    </a>
                    <a href="https://www.tiktok.com/@ridesanddives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-tiktok-fill text-lg"></i>
                      TikTok
                    </a>
                    <a href="https://www.facebook.com/share/g/1AqygJbGTi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-facebook-fill text-lg"></i>
                      Facebook
                    </a>
                    <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 text-sm hover:text-[#8B6F47] transition-colors cursor-pointer py-1">
                      <i className="ri-article-line text-lg"></i>
                      Blog
                    </Link>
                  </div>
                )}
              </div>
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">About</Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="text-sm font-light tracking-widest uppercase hover:text-[#8B6F47] transition-colors cursor-pointer py-2">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[70vh] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/3a7b1db3cd01638d1df6a9e2416e7f58.jpg"
            alt="Merchandise"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black"></div>
        </div>
        <div className="relative h-full flex items-center justify-center text-center px-4 md:px-8 pt-16">
          <div className="max-w-4xl">
            <h1 className="font-serif text-5xl md:text-8xl font-bold leading-tight mb-4 md:mb-6">
              OFFICIAL MERCH
            </h1>
            <p className="text-lg md:text-2xl font-light text-white/80 max-w-2xl mx-auto">
              Wear the journey. Premium apparel and gear for riders who live the lifestyle. More coming soon!
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 md:px-16 bg-black sticky top-16 z-40 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 md:gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'bg-[#8B6F47] text-black font-medium'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-16 md:py-24 px-4 md:px-16 bg-black">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            {filteredProducts.map((product) => (
              <article 
                key={product.id}
                className="group bg-[#1A1A1A] hover:bg-[#222] transition-colors overflow-hidden"
              >
                <div className="relative h-64 md:h-72 overflow-hidden bg-[#0A0A0A]">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  {product.featured && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-[#8B6F47] text-black text-xs uppercase tracking-wider font-medium">
                      Featured
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold group-hover:text-[#8B6F47] transition-colors">
                      {product.name}
                    </h3>
                    <span className="text-[#8B6F47] text-lg font-bold whitespace-nowrap ml-2">
                      ${product.price}
                    </span>
                  </div>

                  <p className="text-white/70 text-sm leading-relaxed mb-4">
                    {product.description}
                  </p>

                  {product.sizes && (
                    <div className="mb-4">
                      <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Available Sizes</p>
                      <div className="flex flex-wrap gap-2">
                        {product.sizes.map((size) => (
                          <span 
                            key={size}
                            className="px-2 py-1 bg-white/5 text-white/60 text-xs"
                          >
                            {size}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setSelectedProduct(product)}
                    className="w-full px-6 py-3 bg-[#8B6F47] text-black text-sm uppercase tracking-wider hover:bg-[#A08558] transition-colors cursor-pointer whitespace-nowrap font-medium"
                  >
                    Add to Cart
                  </button>

                  <Link
                    to={`/merch/${product.slug}`}
                    className="w-full px-6 py-3 bg-transparent border border-white/20 text-white text-sm uppercase tracking-wider hover:border-[#8B6F47] hover:text-[#8B6F47] transition-colors cursor-pointer whitespace-nowrap font-medium text-center block mt-2"
                  >
                    View Details
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 md:py-20 px-4 md:px-16 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 border border-[#8B6F47]">
                <i className="ri-truck-line text-2xl text-[#8B6F47]"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Free Shipping</h3>
              <p className="text-white/60 text-sm">
                Free shipping on all orders over $75. Get your gear delivered fast.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 border border-[#8B6F47]">
                <i className="ri-shield-check-line text-2xl text-[#8B6F47]"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Quality Guaranteed</h3>
              <p className="text-white/60 text-sm">
                Premium materials built to last. If you're not satisfied, we'll make it right.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 border border-[#8B6F47]">
                <i className="ri-arrow-left-right-line text-2xl text-[#8B6F47]"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Easy Returns</h3>
              <p className="text-white/60 text-sm">
                30-day return policy. Wrong size? No problem. We've got you covered.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] pt-12 md:pt-20 pb-8">
        <div className="px-4 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-16 mb-12 md:mb-16">
            <div>
              <div className="flex items-center mb-6 md:mb-8">
                <img 
                  src="https://static.readdy.ai/image/53a123dbe8c72020ea380fa944f94eac/1d9414342dd58a00fdde5ee22dbe4d3e.png" 
                  alt="Rides & Dives" 
                  className="h-16 md:h-20 w-auto"
                />
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <input 
                  type="email" 
                  placeholder="Your email"
                  className="flex-1 px-4 py-3 bg-transparent border border-[#8B6F47] text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#A08558]"
                />
                <button className="px-6 py-3 bg-[#8B6F47] text-black text-sm uppercase tracking-wider hover:bg-[#A08558] transition-colors cursor-pointer whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            </div>
            <div>
              <h3 className="text-[#8B6F47] text-xs tracking-[3px] uppercase mb-6">Explore</h3>
              <div className="space-y-4">
                <Link to="/rides" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Rides</Link>
                <Link to="/dives" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Dives</Link>
                <Link to="/gear" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Gear</Link>
                <Link to="/merch" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Merch</Link>
                <Link to="/about" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">About</Link>
                <Link to="/contact" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Contact</Link>
                <Link to="/partners" className="block text-white text-base hover:text-[#8B6F47] transition-colors cursor-pointer">Become a Partner</Link>
              </div>
            </div>
            <div>
              <h3 className="text-[#8B6F47] text-xs tracking-[3px] uppercase mb-6">Follow</h3>
              <div className="space-y-4">
                <a href="https://www.instagram.com/rides.and.dives/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-2xl"></i>
                  <span className="text-base">Instagram</span>
                </a>
                <a href="https://www.youtube.com/@RidesDives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-youtube-line text-2xl"></i>
                  <span className="text-base">YouTube</span>
                </a>
                <a href="https://www.tiktok.com/@ridesanddives" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-tiktok-line text-2xl"></i>
                  <span className="text-base">TikTok</span>
                </a>
                <a href="https://www.facebook.com/share/g/1AqygJbGTi/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white hover:text-[#8B6F47] transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-2xl"></i>
                  <span className="text-base">Facebook</span>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-[#8B6F47]/30 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/50 text-sm text-center md:text-left">© 2026 Rides & Dives. 130,000 miles and counting.</p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-white/70 text-sm hover:text-white transition-colors cursor-pointer">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Product Modal */}
      {selectedProduct && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-8"
          onClick={() => {
            setSelectedProduct(null);
            setSelectedSize('');
          }}
        >
          <button
            onClick={() => {
              setSelectedProduct(null);
              setSelectedSize('');
            }}
            className="absolute top-4 right-4 md:top-8 md:right-8 w-12 h-12 flex items-center justify-center text-white/70 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>

          <div
            className="max-w-4xl w-full bg-[#1A1A1A] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="relative h-64 md:h-full min-h-[400px] bg-[#0A0A0A]">
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover object-center"
                />
              </div>

              <div className="p-6 md:p-8">
                <h2 className="text-2xl md:text-3xl font-bold mb-2">{selectedProduct.name}</h2>
                <p className="text-[#8B6F47] text-2xl font-bold mb-4">${selectedProduct.price}</p>
                <p className="text-white/70 leading-relaxed mb-6">{selectedProduct.description}</p>

                {selectedProduct.sizes && (
                  <div className="mb-6">
                    <label className="text-white text-sm uppercase tracking-wider mb-3 block">
                      Select Size *
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedProduct.sizes.map((size) => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          className={`px-4 py-3 text-sm uppercase tracking-wider transition-all cursor-pointer ${
                            selectedSize === size
                              ? 'bg-[#8B6F47] text-black font-medium'
                              : 'bg-white/5 text-white/70 hover:bg-white/10'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleAddToCart(selectedProduct)}
                  className="w-full px-8 py-4 bg-[#8B6F47] text-black text-sm uppercase tracking-wider hover:bg-[#A08558] transition-colors cursor-pointer whitespace-nowrap font-medium"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CartPanel isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}
